import { useState } from 'react';
import { FolderDown, Check, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { useApi } from '../api-context';
import { ProgressBar, Spinner } from './Primitives';
import type { Bucket, ExportConflict, ExportProgress, ExportStructure } from '../../types';

export default function ExportDialog({
  projectId,
  buckets,
  onClose,
}: {
  projectId: number;
  buckets: Bucket[];
  onClose: () => void;
}) {
  const api = useApi();
  const keep = buckets.find((b) => b.kind === 'keep');

  const [selected, setSelected] = useState<number[]>(keep ? [keep.id] : []);
  const [structure, setStructure] = useState<ExportStructure>('flat');
  const [conflict, setConflict] = useState<ExportConflict>('rename');
  const [applyRotation, setApplyRotation] = useState(true);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = buckets
    .filter((b) => selected.includes(b.id))
    .reduce((sum, b) => sum + b.count, 0);

  const toggle = (id: number) =>
    setSelected((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const run = async () => {
    setError(null);
    try {
      const destination = await api.pickExportFolder();
      if (!destination) return;

      setRunning(true);
      const result = await api.runExport(
        { projectId, bucketIds: selected, structure, conflict, applyRotation },
        destination,
        setProgress,
      );
      setProgress(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  const finished = progress?.phase === 'done';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/80 p-6"
      onClick={running ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Export"
    >
      <div
        className="card w-full max-w-md animate-rise p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-head font-medium">Export photos</h2>
        <p className="mt-1 text-caption text-ink-400">
          Copies go into a folder you choose. Your originals are never modified.
        </p>

        <section className="mt-5">
          <h3 className="label">Which photos</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {buckets.map((bucket) => {
              const on = selected.includes(bucket.id);
              return (
                <button
                  key={bucket.id}
                  onClick={() => toggle(bucket.id)}
                  disabled={running}
                  className={clsx(
                    'btn border',
                    on && bucket.kind === 'keep' && 'border-keep-line bg-keep-soft text-keep',
                    on && bucket.kind === 'reject' && 'border-reject-line bg-reject-soft text-reject',
                    !on && 'border-ink-700 text-ink-300 hover:border-ink-600',
                  )}
                >
                  {on && <Check size={13} />}
                  {bucket.name}
                  <span className="tnum text-ink-500">{bucket.count}</span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-5 grid grid-cols-2 gap-4">
          <div>
            <h3 className="label">Structure</h3>
            <Segmented
              disabled={running}
              value={structure}
              onChange={setStructure}
              options={[
                { value: 'flat', label: 'One folder' },
                { value: 'by-bucket', label: 'By bucket' },
              ]}
            />
          </div>
          <div>
            <h3 className="label">If a name exists</h3>
            <Segmented
              disabled={running}
              value={conflict}
              onChange={setConflict}
              options={[
                { value: 'rename', label: 'Rename' },
                { value: 'skip', label: 'Skip' },
              ]}
            />
          </div>
        </section>

        <label className="mt-5 flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={applyRotation}
            disabled={running}
            onChange={(event) => setApplyRotation(event.target.checked)}
            className="mt-0.5 h-4 w-4 accent-ink-100"
          />
          <span>
            <span className="text-caption text-ink-100">Apply rotations</span>
            <span className="block text-tiny text-ink-500">
              Written into the copy&rsquo;s EXIF tag, so no pixels are re-encoded.
            </span>
          </span>
        </label>

        {progress && (
          <div className="mt-5">
            <ProgressBar value={progress.done} total={progress.total} />
            <p className="tnum mt-2 text-tiny text-ink-400">
              {finished
                ? progress.message
                : `${progress.done} of ${progress.total}${progress.failed ? ` · ${progress.failed} failed` : ''}`}
            </p>
          </div>
        )}

        {progress && progress.errors.length > 0 && (
          <div className="mt-3 max-h-28 overflow-y-auto rounded border border-reject-line bg-reject-soft p-2.5">
            {progress.errors.slice(0, 8).map((item) => (
              <p key={item.filename} className="text-tiny text-reject">
                <AlertTriangle size={11} className="mr-1 inline" />
                {item.filename}: {item.error}
              </p>
            ))}
          </div>
        )}

        {error && <p className="mt-3 text-tiny text-reject">{error}</p>}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button className="btn-ghost" onClick={onClose} disabled={running}>
            {finished ? 'Done' : 'Cancel'}
          </button>
          <button
            className="btn-primary"
            onClick={run}
            disabled={running || selected.length === 0 || count === 0}
          >
            {running ? <Spinner className="h-4 w-4" /> : <FolderDown size={15} />}
            {running ? 'Copying…' : `Choose folder & export ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
  disabled,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div className="mt-2 flex rounded border border-ink-700 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={clsx(
            'flex-1 rounded-[3px] px-2 py-1.5 text-tiny transition',
            option.value === value ? 'bg-ink-750 text-ink-50' : 'text-ink-400 hover:text-ink-100',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
